import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ReportBaseComponent } from '../report-base.component';
import { TestResult_PatientsByResultRow } from '../../../services/search.service.testresult.models';
import { MetadataService } from '../../../services/metadata.service';
import { InitDataSelectionLists, TestType, AnalyteDetail } from '../../../services/metadata.service.models';
import { BusyService } from '../../../services/busy.service';
import { ChartType } from 'angular-google-charts';

@Component({
  selector: 'core-patients-result',
  templateUrl: './patients-result.component.html',
  styleUrls: ['./patients-result.component.scss'],
  standalone: false
})
export class PatientsResultComponent extends ReportBaseComponent implements OnInit, OnDestroy {

  @Input() data: TestResult_PatientsByResultRow[];
  showChart: boolean;
  chartType: ChartType;
  chartTitle: string;
  chartData: Array<Array<string | number>>;
  chartOptions: Record<string, unknown>;
  chartRoles: Array<{ type: string; role: string; index?: number }>;
  chartColumnNames: Array<string>;

  testTypes: TestType[];
  analyteDetails: AnalyteDetail[];

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    elementRef: ElementRef<HTMLElement>,
    busyService: BusyService,
    private metadataService: MetadataService) {
    super(changeDetectorRef, translateService, 'PatientsByResult', busyService, elementRef);
    this.showChart = false;
  }

  ngOnInit(): void {

    this.subscription.add(this.metadataService.fetchInitDataSelectionLists().subscribe((initData: InitDataSelectionLists) => {
      this.testTypes = initData.TestTypes;
      this.analyteDetails = initData.Analytes;

      if (this.data) {
        this.setData(this.data);
      }
    }));

    this.chartType = ChartType.ColumnChart;
    this.chartOptions = {
      legend: 'right',
      bar: { groupWidth: '75%' },
      isStacked: true,
      hAxis: {
        title: this.getText('HAxis'),
        slantedText: true,
        slantedTextAngle: 90,
        showTextEvery: 1,
      },
      vAxis: {
        title: this.getText('VAxis')
      },
      chartArea: { left: 150, right: 250, top: 40, bottom: 200 }
    };
    this.chartData = [[]];
  }

  ngOnDestroyInternal(): void {
    // Required by base component
  }

  onTranslateResourcesLoaded(): void {
    this.setTitle();
  }

  onTranslateLanguageChanged(): void {
    this.setTitle();
  }

  private setTitle() {
    this.chartTitle = this.getText('Title');
  }

  public setData(data: TestResult_PatientsByResultRow[]): void {
    this.beginSetData();
    this.data = data;

    if (!this.testTypes) {
      return;
    }

    const columnNames = ['Result'];
    const results = [];
    const convData: (string | number)[][] = [];
    const series = [];

    data.forEach((row) => {
      let resultNdx = results.indexOf(row.result);
      if (resultNdx < 0) {
        results.push(row.result);
        resultNdx = results.length - 1;
        convData.push([row.result]);
      }

      let columnName: string;

      if (row.analyteName && this.data.filter(r => r.testName === row.testName).length > 1) {
        columnName = row.testName + ' - ' + row.analyteName;
      } else if (row.analyteName) {
        columnName = row.analyteName;
      } else {
        columnName = row.testName;
      }

      let columnNameNdx = columnNames.indexOf(columnName);
      if (columnNameNdx < 0) {
        columnNames.push(columnName);
        columnNameNdx = columnNames.length - 1;

        const test = this.testTypes.find(t => t.testName === row.testName);
        const analyte = this.analyteDetails.find(a => a.name === row.analyteName);

        series.push({ color: analyte ? analyte.positiveColor : test ? test.color : '#98999b' });
      }

      const dataRow = convData[resultNdx];
      while (dataRow.length < columnNameNdx - 1) {
        dataRow.push(0);
      }
      dataRow[columnNameNdx] = row.testCount;
      convData[resultNdx] = dataRow;
    });

    // Fill in holes
    convData.forEach((dataRow) => {
      while (dataRow.length < columnNames.length) {
        dataRow.push(0);
      }
    });

    this.chartColumnNames = columnNames;
    this.chartData = convData;
    this.chartOptions.series = series;

    this.showChart = true;
  }
}
